import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { FormattedMessage, FormattedNumber, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType } from '@liveauctioneers/hammer-ui-core/select';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import TimedOrCatalogCard from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCatalogCard/TimedOrCatalogCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { SearchSortPageSizeFilter } from '@/components/Pagination/hammer-pagination/SearchSortPageSizeFilter';
import { PAGE_ITEMS_TO_VIEW, PAGE_ITEMS_TO_VIEW_PLUS_ALL } from '@/components/Pagination/helpers/filterSortOptions';
import { useShouldShowPurchaseProtectionBanner } from '@/components/PurchaseProtection/PurchaseProtectionBanner/hooks/useShouldShowPurchaseProtectionBanner';
import { PurchaseProtectionBanner } from '@/components/PurchaseProtection/PurchaseProtectionBanner/PurchaseProtectionBanner';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import {
    getCatalogIsLAPayments,
    getCatalogIsTimedPlus,
    getCatalogSellerId,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogDetailContainsBuyNowItems } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { fetchCatalogItemSummaries } from '@/redux/modules/catalog/items/catalogItems.actions';
import {
    getCatalogItemIdsByCatalogId,
    getCatalogItemsLoading,
    getCatalogItemsPagination,
} from '@/redux/modules/catalog/items/catalogItems.selectors';
import { fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { changePaginationPageSize, getSearchPageSize } from '@/redux/modules/pagination';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { FilterAction } from '@/types/SearchFacet';
import { getPageFromQueryParams, parseQueryParams } from '@/utils/queryParams';
import TEST_HOUSES from '@/utils/testHouses';
import { toQueryString } from '@/utils/urls';

import { getFilteredSortOrders } from './helpers/getSortOptions';
import normalizeQuery from './helpers/normalizeQuery';

const CATALOG_ITEMS_GRID_ID = 'catalog-items-grid';
const CATALOG_ITEMS_GRID_OFFSET_TOP = -130;

interface QueryParams {
    buyNow?: string;
    keyword?: string;
    page?: string;
    pageSize?: string;
    preventScrollReset?: boolean;
    sort?: string;
}

const PAGE_SIZE_SELECTOR_OPTIONS = PAGE_ITEMS_TO_VIEW_PLUS_ALL.map((option) => ({
    label: `${option}`.charAt(0).toUpperCase() + `${option}`.slice(1),
    value: option,
}));

type Props = {
    catalogId: number;
};

const CatalogPageItems = ({ catalogId }: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { search } = useLocation();
    const navigate = useNavigate();

    const params: QueryParams = parseQueryParams(search);
    const page = getPageFromQueryParams(params);
    const pageSize = useAppSelector(getSearchPageSize);
    const loading = useAppSelector(getCatalogItemsLoading);
    const pagination = useAppSelector(getCatalogItemsPagination);
    const itemIds = useAppSelector((state) => getCatalogItemIdsByCatalogId(state, catalogId));
    const containsBuyNowItems = useAppSelector((state) => getCatalogDetailContainsBuyNowItems(state, catalogId));
    const buyNow = params.buyNow !== 'false' && typeof params.buyNow !== 'undefined';
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const livePaymentsEnabled = useAppSelector((state) => getCatalogIsLAPayments(state, catalogId));

    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const bidderId = useAppSelector(getBidderId);
    const previousBidderId = usePrevious(bidderId);
    const shouldShowBanner = useShouldShowPurchaseProtectionBanner() && livePaymentsEnabled;

    useEffect(() => {
        if (isTimedPlus && (!previousBidderId || previousBidderId <= 0) && Boolean(bidderId) && bidderId > 0) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(
                fetchCatalogItemSummaries({
                    catalogId,
                    query: {
                        buyNow: params.buyNow === 'true',
                        keyword: params.keyword ?? '',
                        page: isNaN(Number(params.page)) ? 1 : Number(params.page),
                        pageSize: isNaN(Number(params.pageSize)) ? 24 : Number(params.pageSize),
                        preview: TEST_HOUSES.includes(sellerId),
                        // the sort param comes from a dropdown.
                        // so it can be used as-is
                        sort: (params.sort as SearchSortAndDirection) ?? SearchSortAndDirection.LotNumberLowest,
                    },
                })
            );
        }
    }, [bidderId, catalogId, dispatch, isTimedPlus, params, previousBidderId, sellerId]);

    const onChange = (param: QueryParams) => {
        const newParams = normalizeQuery({ ...params, ...param });

        if (typeof newParams.pageSize !== 'undefined') {
            dispatch(changePaginationPageSize(newParams.pageSize));
        }

        const sortChanged = 'sort' in param && (params.sort ?? SearchSortAndDirection.LotNumberLowest) !== param.sort;
        const buyNowChanged = 'buyNow' in param && (params.buyNow === 'true') !== (param.buyNow === 'true');
        const pageSizeChanged = 'pageSize' in param && String(params.pageSize ?? 24) !== String(param.pageSize);

        let filterAction: FilterAction | undefined;
        if (sortChanged) {
            filterAction = FilterAction.SortByChanged;
        } else if (buyNowChanged) {
            filterAction = FilterAction.BuyingFormatChanged;
        } else if (pageSizeChanged) {
            filterAction = FilterAction.PageSizeChanged;
        }

        navigate(
            { search: toQueryString(newParams) },
            {
                state: {
                    filterAction,
                    preventScrollReset: param.preventScrollReset,
                    // Offset top is to account for the filters at the top of the page
                    // Only trigger this if interacting with bottom pagination buttons
                    scrollToId: !param.preventScrollReset
                        ? { id: CATALOG_ITEMS_GRID_ID, offsetTop: CATALOG_ITEMS_GRID_OFFSET_TOP }
                        : undefined,
                },
            }
        );
    };

    const sortOptions: OptionType[] = getFilteredSortOrders({
        catalogStatus,
    }).map((key) => ({
        label: formatMessage({ id: `sorts.${key}` }),
        value: key,
    }));

    return (
        <div
            className="relative mx-auto w-[calc(100%-40px)] max-w-[1240px]"
            id={CATALOG_ITEMS_GRID_ID}
        >
            {shouldShowBanner && <PurchaseProtectionBanner />}
            <Pagination
                filter={
                    <>
                        <SearchSortPageSizeFilter
                            className="mb-lg"
                            loading={loading}
                            onChange={onChange}
                            pageSizeOptions={PAGE_SIZE_SELECTOR_OPTIONS}
                            paginationFilter={pagination}
                            resultsDisplay={
                                pagination.keyword && (
                                    <BodyPrimary>
                                        <FormattedMessage
                                            id="search_alerts.resultsFor"
                                            values={{
                                                count: (
                                                    <FormattedNumber
                                                        key="results_for_count"
                                                        value={pagination.totalRecords}
                                                    />
                                                ),
                                                search: pagination.keyword,
                                            }}
                                        />
                                    </BodyPrimary>
                                )
                            }
                            searchPlaceholder={formatMessage(
                                {
                                    id: 'search_alerts.resultsPlaceholderExtended',
                                },
                                { count: pagination.totalRecords }
                            )}
                            shouldModifyQueryParams={false}
                            sortOptions={sortOptions}
                        />
                        {containsBuyNowItems && (
                            <Checkbox.Option
                                checked={buyNow}
                                className="m-sm"
                                name="buy-now-checkbox"
                                onClick={() => onChange({ buyNow: `${!buyNow}` })}
                            >
                                {formatMessage({ id: 'filter_buy_now' })}
                            </Checkbox.Option>
                        )}
                    </>
                }
                onChange={onChange}
                pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                paginationFilter={pagination}
            >
                <FluidItemGrid
                    itemCardOption={TimedOrCatalogCard}
                    itemIds={itemIds}
                    lazyLoadImage={!isTimedPlus}
                    page={page}
                    pageSize={pageSize}
                />
            </Pagination>
        </div>
    );
};

export default CatalogPageItems;
